import { useMemo } from "react";
import type { BoardColumn, Task } from "../types";
import {
  arrangeByPlacement,
  type ListlessGroupPlacement,
} from "../lib/listlessGroupPlacement";

interface UseBoardVisibleTasksArgs {
  columns: BoardColumn[];
  listlessTasks: Task[];
  matchingTaskIds: Set<string>;
  listlessPlacement: ListlessGroupPlacement;
}

/**
 * The tasks the board shows, in reading order: each list's matching tasks,
 * with the listless ones placed where the user chose. The task dialog's
 * previous/next follows this order. On a Today filter the arrows stop on
 * the tasks and sub-tasks dated today, not on a task shown only so those
 * sub-tasks have a place. A collapsed list's tasks stay in
 * it: the dialog shows one task at a time, so a card folded away on the
 * board is no reason to skip it — and a task opened by link or refresh into
 * a collapsed list still has neighbours to step to.
 */
export function useBoardVisibleTasks({
  columns,
  listlessTasks,
  matchingTaskIds,
  listlessPlacement,
}: UseBoardVisibleTasksArgs): Task[] {
  return useMemo(
    () =>
      arrangeByPlacement(
        columns.flatMap((column) =>
          column.tasks.filter((t) => matchingTaskIds.has(t.id)),
        ),
        listlessTasks.filter((t) => matchingTaskIds.has(t.id)),
        listlessPlacement,
      ),
    [columns, listlessTasks, matchingTaskIds, listlessPlacement],
  );
}
